import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  chakra,
  Flex,
  HStack,
  Td,
  Text,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiChat1Line } from "@remixicon/react";
import React from "react";
import { Link } from "react-router";

import CompanyDisplay from "@/common/components/CompanyDisplay";
import ExpandableTextCell from "@/common/components/ExpandableTextCell";
import { IcoAutoFeedbackSurveys } from "@/common/components/Icons";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import { SectionHeading } from "@/common/components/SectionHeading";
import SimpleTable from "@/common/components/SimpleTable";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSlugParam } from "@/common/hooks/useParam";
import { EmptyMonitorState } from "@/feature/components/EmptyMonitorState";
import SatisfactionHistogram from "@/feature/components/SatisfactionHistogram";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureFeedbackPlaceholderData } from "@/feature/data/useMonitorPlaceholderData";
import { MONITOR_CARD_HEIGHT } from "@/feature/pages/FeatureMonitor";
import SatisfactionCell from "@/feedback/components/SatisfactionCell";
import { SatisfactionTimeline } from "@/feedback/components/SatisfactionTimeline";
import { useFeedbackData } from "@/feedback/data/useFeedbackData";
import { FeedbackDTO, FeedbackList } from "@reflag/shared/feedbackAPI";
import { FeatureSettingsUrl, FeedbackUrl } from "@reflag/shared/urls";

const FeedbackIcon = chakra(RiChat1Line, {
  baseStyle: {
    boxSize: 4,
    _dark: {
      color: "gray.500",
    },
    color: "gray.400",
  },
});

export const FeatureFeedback = () => {
  const { featureId } = useSlugParam("feature");
  const { env } = useEnvironment();

  const { data: feature, isSuccess: featureIsSuccess } =
    useFeatureData(featureId);
  const params = {
    sortBy: "timestamp" as const,
    sortOrder: "desc" as const,
    pageSize: 5,
    featureId,
  };
  const { data, isLoading, isError, refetch } = useFeedbackData(params);
  const placeholderData = useFeatureFeedbackPlaceholderData(params);

  const brandColor = useColorModeValue("brand.400", "brand.300");

  const hasData = !!data?.data.length;

  return (
    <Flex direction="column" gap={4}>
      <SectionHeading
        fontSize="lg"
        icon={<FeedbackIcon />}
        rightElement={
          <ButtonGroup alignItems="center" gap={2}>
            {!featureIsSuccess ? null : (
              <Link to={FeatureSettingsUrl(env, feature, "feedback")}>
                <HStack
                  color={
                    feature.autoFeedbackSurveysEnabled ? brandColor : "dimmed"
                  }
                  spacing={1}
                >
                  <IcoAutoFeedbackSurveys size="md" />
                  <Text fontSize="sm">
                    Auto surveys are{" "}
                    {feature.autoFeedbackSurveysEnabled ? "on" : "off"}
                  </Text>
                </HStack>
              </Link>
            )}
            <Button
              as={Link}
              to={FeedbackUrl(env, { feature: featureId })}
              variant="outline"
            >
              Explore feedback
            </Button>
          </ButtonGroup>
        }
      >
        Feedback
      </SectionHeading>

      <Card>
        <CardBody display="flex" flexDirection="column" p={0}>
          <EmptyMonitorState
            hasData={hasData}
            isError={isError}
            isLoading={isLoading}
            minH={MONITOR_CARD_HEIGHT}
            usecase="feedback"
            onRetry={refetch}
          >
            <RecentResponsesTable data={hasData ? data : placeholderData} />
            <SatisfactionStatistics featureId={featureId!} />
          </EmptyMonitorState>
        </CardBody>
      </Card>
    </Flex>
  );
};

function RecentResponsesTable({ data }: { data?: FeedbackList }) {
  const users = data?.data || [];

  const columns = ["Score", "Comment", "User", "Company", "Received"];

  const rowTemplate = (feedback: FeedbackDTO, index: number) => (
    <Tr key={index}>
      <Td verticalAlign="top">
        {feedback.score !== null ? (
          <SatisfactionCell value={feedback.score} />
        ) : (
          <NotAvailableCell />
        )}
      </Td>
      <Td minW="280px" verticalAlign="top" w="full">
        {feedback.comment ? (
          <ExpandableTextCell value={feedback.comment} />
        ) : (
          <Text color="dimmed" fontSize="sm">
            No comment
          </Text>
        )}
      </Td>
      <Td verticalAlign="top">
        {feedback.userId ? (
          <UserDisplay
            link
            user={{
              id: feedback.userId,
              name: feedback.userName,
              avatarUrl: feedback.userAvatarUrl,
            }}
          />
        ) : (
          <NotAvailableCell />
        )}
      </Td>
      <Td verticalAlign="top">
        {feedback.companyId ? (
          <CompanyDisplay
            company={{
              id: feedback.companyId,
              name: feedback.companyName,
              avatarUrl: feedback.companyAvatarUrl,
            }}
            fontSize="sm"
            link
          />
        ) : (
          <NotAvailableCell />
        )}
      </Td>
      <Td verticalAlign="top">
        {feedback.timestamp !== undefined ? (
          <TimestampCell value={feedback.timestamp} leftAlign />
        ) : (
          <NotAvailableCell />
        )}
      </Td>
    </Tr>
  );

  return (
    <Box overflowX="auto">
      <SimpleTable
        colProps={[
          { style: { width: "auto" } },
          { style: { width: "280px" } },
          { style: { width: "auto" } },
          { style: { width: "auto" } },
          { style: { width: "auto" } },
        ]}
        columns={columns}
        minH={MONITOR_CARD_HEIGHT}
        rows={users}
        rowTemplate={rowTemplate}
      />
    </Box>
  );
}

function SatisfactionStatistics({ featureId }: { featureId: string }) {
  return (
    <Flex direction="row" gap={12} p={6}>
      <SatisfactionHistogram
        _sectionHeading={{
          h: 8,
          alignContent: "center",
        }}
        featureId={featureId}
        maxW="xs"
        minW="2xs"
      />
      <SatisfactionTimeline
        featureId={featureId}
        flexGrow={1}
        minH={70}
        title={null}
      />
    </Flex>
  );
}
